import React, { useState, useEffect, useCallback } from 'react';
import MapView from '../components/MapView';
import Sidebar from '../components/Sidebar';
import ElevationProfilePanel from '../components/ElevationProfilePanel';
import { GPXData, AidStation, RouteSegment, GpxPoint } from '../types';
import { parseGPX } from '../utils/gpxParser';
import { parseAidStations } from '../utils/dataLoader';
import { calculateSegments } from '../utils/routeUtils';
import '../App.css';

// Define type for hovered location state
type HoveredLocation = [number, number] | null;

// Function to map coordinate tuples to GpxPoint objects
const mapCoordsToGpxPoints = (coords: [number, number, number?][] | undefined): GpxPoint[] => {
  if (!coords) return [];
  return coords.map(([lon, lat, ele]) => ({
    lat: lat,
    lon: lon,
    ele: ele ?? 0,
  }));
};

function HomePage() {
  const [gpxData, setGpxData] = useState<GPXData | null>(null);
  const [rawGpxString, setRawGpxString] = useState<string | null>(null);
  const [aidStations, setAidStations] = useState<AidStation[]>([]);
  const [segments, setSegments] = useState<RouteSegment[]>([]);
  const [isElevationPanelOpen, setIsElevationPanelOpen] = useState(false);
  const [hoveredLocation, setHoveredLocation] = useState<HoveredLocation>(null);

  const handleSetGpxData = useCallback((data: GPXData | null) => {
    setGpxData(data);
  }, []);

  const handleSetRawGpxString = useCallback((gpxString: string | null) => {
    setRawGpxString(gpxString);
  }, []);

  const handleAddWaypoint = (waypoint: AidStation) => {
    setAidStations(prev => [...prev, { ...waypoint, pcNumber: 0 }]);
  };

  const toggleElevationPanel = () => {
    setIsElevationPanelOpen(prev => !prev);
  };

  useEffect(() => {
    // Initial Load Example
    fetch('/petzl-trail-plus-70km.gpx') 
      .then(async response => {
          if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
          }
          const gpxString = await response.text();
          handleSetRawGpxString(gpxString);
          const parsedData = parseGPX(gpxString);
          console.log('[HomePage.tsx] Initial Parsed GPX Data:', parsedData);
          handleSetGpxData(parsedData);
      })
      .catch(error => console.error('Error loading initial GPX file:', error));

    // Load aid stations
    fetch('/aidstations.txt')
      .then(response => response.text())
      .then(text => {
        const stations = parseAidStations(text);
        setAidStations(stations);
      })
      .catch(error => console.error('Error loading aid stations:', error));
  }, [handleSetGpxData, handleSetRawGpxString]);

  useEffect(() => {
    if (gpxData && aidStations.length > 0) {
      const newSegments = calculateSegments(gpxData, aidStations);
      setSegments(newSegments);
    }
  }, [gpxData, aidStations]);

  return (
    <div className="App">
      <Sidebar
        gpxData={gpxData}
        setGpxData={handleSetGpxData}
        rawGpxString={rawGpxString}
        setRawGpxString={handleSetRawGpxString}
        aidStations={aidStations}
        setAidStations={setAidStations}
        segments={segments}
        setSegments={setSegments}
        isElevationPanelOpen={isElevationPanelOpen}
        toggleElevationPanel={toggleElevationPanel}
      />
      <main className="main-content">
        <MapView 
            gpxData={gpxData} 
            aidStations={aidStations} 
            segments={segments} 
            onAddWaypoint={handleAddWaypoint}
            hoveredLocation={hoveredLocation}
        />
        {isElevationPanelOpen && gpxData && gpxData.coordinates && (
          <ElevationProfilePanel 
            gpxPoints={mapCoordsToGpxPoints(gpxData.coordinates)}
            isOpen={isElevationPanelOpen}
            setHoveredLocation={setHoveredLocation} 
          />
        )}
      </main>
    </div>
  );
}

export default HomePage; 